import React from "react";
import { SUPPLIERS, SKUS } from "../lib/data";
import { IMPORT_LOGISTICS, DELIVERY_ROUTES, ROUTE_SAVINGS } from "../lib/dataUseCases";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ScatterChart, Scatter, ZAxis,
} from "recharts";
import { CheckCircle2, Clock, AlertTriangle, Ship, Truck, Anchor, Package, MapPin } from "lucide-react";
import { useChartColors } from "../lib/chartColors";
import { OriginScope, OriginSummary, OriginBadge, syn } from "../lib/origin.jsx";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

// AI-generated reorder recommendations (sample)
const REORDER_LIST = [
  { id: "PO-DRAFT-001", sku: "Sanden SD7H15 Compressor", supplier: "Sanden International", units: 240, value: 4_320_000, urgency: "critical", branches: 8, reason: "Stockout in 8 branches; 14-day cover" },
  { id: "PO-DRAFT-002", sku: "Freon R32 9.5kg", supplier: "Honeywell Refrigerants", units: 1800, value: 5_940_000, urgency: "critical", branches: 24, reason: "Heat advisory + Shopee promo upcoming" },
  { id: "PO-DRAFT-003", sku: "Inverter Compressor 1.5HP", supplier: "GMCC Compressors", units: 380, value: 6_080_000, urgency: "warning", branches: 12, reason: "Forecast +24% next 60d; lead time 55d" },
  { id: "PO-DRAFT-004", sku: "Auto Compressor TM-16", supplier: "Capella Industrial", units: 180, value: 3_240_000, urgency: "warning", branches: 6, reason: "Supplier delay risk; safety stock thinning" },
  { id: "PO-DRAFT-005", sku: "Refrig Compressor 1/4HP", supplier: "Matsushita / Panasonic", units: 320, value: 2_880_000, urgency: "info", branches: 15, reason: "Seasonal pre-position for Q1" },
];

const OPEN_POS = [
  { id: "PO-2026-0148", supplier: "Sanden International", value: 8_240_000, eta: "Mar 14, 2026", status: "in-transit", days: 22 },
  { id: "PO-2026-0149", supplier: "Capella Industrial", value: 12_480_000, eta: "Apr 02, 2026", status: "delayed", days: 41 },
  { id: "PO-2026-0150", supplier: "Honeywell Refrigerants", value: 5_640_000, eta: "Mar 28, 2026", status: "in-transit", days: 35 },
  { id: "PO-2026-0151", supplier: "Matsushita / Panasonic", value: 9_120_000, eta: "Apr 18, 2026", status: "production", days: 58 },
  { id: "PO-2026-0152", supplier: "Superlon Worldwide", value: 1_840_000, eta: "Mar 04, 2026", status: "in-transit", days: 12 },
  { id: "PO-2026-0153", supplier: "GMCC Compressors", value: 6_980_000, eta: "Apr 22, 2026", status: "production", days: 62 },
  { id: "PO-2026-0154", supplier: "Local Manila Distributors", value: 420_000, eta: "Feb 12, 2026", status: "received", days: 5 },
];

const STATUS_CONF = {
  "in-transit": { color: "info", icon: Truck, label: "In Transit" },
  "delayed": { color: "critical", icon: AlertTriangle, label: "Delayed" },
  "production": { color: "warning", icon: Clock, label: "In Production" },
  "received": { color: "success", icon: CheckCircle2, label: "Received" },
};

export default function ProcurementSupply() {
  return (
    <OriginScope>
      <ProcurementBody />
    </OriginScope>
  );
}

function ProcurementBody() {
  const C = useChartColors();
  const totalOpen = OPEN_POS.reduce((s, p) => s + p.value, 0);
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART V + VII · Stream 3" title="Procurement & Supply Chain"
        description="Open POs, supplier OTD, landed-cost analytics, and the AI-generated weekly procurement action list." />

      <OriginSummary />

      <div className="tile grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
        <Agg testid="kpi-open-pos" origin={syn("open purchase orders")} label="Open POs" value={OPEN_POS.length} icon={<Ship size={14} />} />
        <Agg testid="kpi-open-value" origin={syn("open purchase orders")} label="Open Value" value={formatPeso(totalOpen, { compact: true })} />
        <Agg testid="kpi-supplier-otd" origin={syn("supplier receipts vs PO dates")} label="Supplier OTD" value="84.2%" status="warning" />
        <Agg testid="kpi-lead-time" origin={syn("supplier lead times")} label="Avg Lead Time" value="58 days" />
        <Agg testid="kpi-in-production" origin={syn("open purchase orders")} label="In Production" value={OPEN_POS.filter(p => p.status === "production").length} status="warning" />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* AI Reorder list */}
        <Tile className="col-span-12 xl:col-span-7" testid="reorder-list" origin={syn("weekly AI reorder list")}
              title="AI Reorder Recommendations — Weekly Action List"
              action={<Badge status="info">Generated 12 min ago</Badge>}>
          <div className="space-y-0 divide-y divide-border -mx-4 -my-4">
            {REORDER_LIST.map(r => (
              <div key={r.id} className="px-4 py-3 flex items-start gap-3">
                <StatusDot status={r.urgency} className="mt-1.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.sku}</span>
                    <Badge status="neutral">{r.id}</Badge>
                  </div>
                  <div className="text-xs text-muted mt-0.5">{r.supplier} · {r.branches} branches affected</div>
                  <div className="text-[11px] text-fg/80 mt-1.5 italic">→ {r.reason}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-tabular text-sm font-semibold">{formatPeso(r.value, { compact: true })}</div>
                  <div className="text-[11px] text-muted font-mono-tabular">{formatNumber(r.units)} units</div>
                </div>
                <div className="flex flex-col gap-1.5 ml-2">
                  <button data-testid={`approve-${r.id}`} className="text-[11px] px-2 py-1 bg-primary text-primary-fg rounded-sm hover:opacity-90">Approve</button>
                  <button className="text-[11px] px-2 py-1 border border-border text-muted hover:text-fg rounded-sm">Defer</button>
                </div>
              </div>
            ))}
          </div>
        </Tile>

        {/* Supplier OTD scatter */}
        <Tile className="col-span-12 xl:col-span-5" testid="supplier-risk-matrix" origin={syn("supplier OTD × lead time")} title="Supplier Risk Matrix">
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
                <XAxis type="number" dataKey="leadTime" name="Lead Time (d)" tick={tickStyle} stroke={C.borderStrong}
                       label={{ value: "Lead Time (days)", position: "bottom", offset: 0, fill: C.muted, fontSize: 10 }} />
                <YAxis type="number" dataKey="otd" name="OTD %" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${Math.round(v * 100)}%`}
                       domain={[0.6, 1]} label={{ value: "OTD %", angle: -90, position: "insideLeft", fill: C.muted, fontSize: 10 }} />
                <ZAxis type="number" dataKey="totalValue" range={[60, 600]} />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<TT formatter={v => typeof v === "number" && v < 1 ? `${(v * 100).toFixed(0)}%` : v} />} />
                <Scatter data={SUPPLIERS} fill={C.chart[1]} stroke={C.chart[0]} strokeWidth={1.5} isAnimationActive={false} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted text-center mt-2">Bubble size = annual spend. Top-right = high OTD, manageable lead-time.</div>
        </Tile>

        <Tile className="col-span-12" testid="open-pos" origin={syn("open purchase orders")} title="Open Purchase Orders">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  <th className="text-left py-2 px-3 border-b border-border">PO #</th>
                  <th className="text-left py-2 px-3 border-b border-border">Supplier</th>
                  <th className="text-left py-2 px-3 border-b border-border">Status</th>
                  <th className="text-right py-2 px-3 border-b border-border">Value</th>
                  <th className="text-right py-2 px-3 border-b border-border">Days Outstanding</th>
                  <th className="text-right py-2 px-3 border-b border-border">ETA</th>
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {OPEN_POS.map(po => {
                  const c = STATUS_CONF[po.status];
                  return (
                    <tr key={po.id} className="border-b border-border last:border-b-0 hover:bg-surface-2">
                      <td className="py-2 px-3 font-semibold">{po.id}</td>
                      <td className="py-2 px-3 font-sans">{po.supplier}</td>
                      <td className="py-2 px-3"><Badge status={c.color}><c.icon size={11} /> {c.label}</Badge></td>
                      <td className="py-2 px-3 text-right">{formatPeso(po.value, { compact: true })}</td>
                      <td className={cn("py-2 px-3 text-right", po.days > 40 ? "text-warning" : "text-fg")}>{po.days}d</td>
                      <td className="py-2 px-3 text-right">{po.eta}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Tile>

        <Tile className="col-span-12" testid="supplier-performance" origin={syn("supplier scorecard")} title="Supplier Performance">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  <th className="text-left py-2 px-3 border-b border-border">Supplier</th>
                  <th className="text-left py-2 px-3 border-b border-border">Country</th>
                  <th className="text-left py-2 px-3 border-b border-border">Category</th>
                  <th className="text-right py-2 px-3 border-b border-border">Lead Time</th>
                  <th className="text-right py-2 px-3 border-b border-border">OTD</th>
                  <th className="text-right py-2 px-3 border-b border-border">Rating</th>
                  <th className="text-right py-2 px-3 border-b border-border">Open POs</th>
                  <th className="text-right py-2 px-3 border-b border-border">Annual Spend</th>
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {SUPPLIERS.map(s => (
                  <tr key={s.id} className="border-b border-border last:border-b-0 hover:bg-surface-2">
                    <td className="py-2 px-3 font-sans font-medium">{s.name}</td>
                    <td className="py-2 px-3">{s.country}</td>
                    <td className="py-2 px-3"><Badge status="neutral">{s.category}</Badge></td>
                    <td className="py-2 px-3 text-right">{s.leadTime}d</td>
                    <td className={cn("py-2 px-3 text-right font-semibold", s.otd >= 0.85 ? "text-success" : s.otd >= 0.75 ? "text-warning" : "text-critical")}>
                      {(s.otd * 100).toFixed(0)}%
                    </td>
                    <td className="py-2 px-3 text-right">{s.rating.toFixed(1)}</td>
                    <td className="py-2 px-3 text-right">{s.openPOs}</td>
                    <td className="py-2 px-3 text-right">{formatPeso(s.totalValue, { compact: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tile>

        {/* Import Logistics Timeline */}
        <Tile className="col-span-12" testid="import-logistics" origin={syn("vessel timeline & customs")}
              title="Import Logistics Intelligence — Vessel Timeline & Customs"
              action={<Badge status="warning">{IMPORT_LOGISTICS.filter(p => p.delayRisk > 5).length} POs at-risk</Badge>}>
          <div className="space-y-4">
            {IMPORT_LOGISTICS.map(po => (
              <div key={po.poId} className="p-3 border border-border rounded-sm bg-surface-2/40" data-testid={`import-${po.poId}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-mono-tabular text-sm font-semibold">{po.poId}</div>
                    <div className="text-[11px] text-muted">{po.supplier} · {po.skus} SKUs · {formatNumber(po.units)} units · {formatPeso(po.value, { compact: true })}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-[10px] text-muted uppercase tracking-widest">ETA Confidence</div>
                      <div className={cn("font-mono-tabular font-semibold text-sm",
                        po.etaConfidence >= 0.8 ? "text-success" : po.etaConfidence >= 0.6 ? "text-warning" : "text-critical")}>
                        {(po.etaConfidence * 100).toFixed(0)}%
                      </div>
                    </div>
                    {po.delayRisk > 0 && <Badge status={po.delayRisk > 10 ? "critical" : "warning"}>+{po.delayRisk}d delay</Badge>}
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-1 relative">
                  {po.stages.map((st, i) => {
                    const ic = {
                      "Factory": Package, "Port Load": Anchor, "Vessel": Ship,
                      "Customs": AlertTriangle, "DC Receipt": Truck,
                    }[st.stage] || Package;
                    const Icon = ic;
                    const isComplete = st.status === "complete";
                    const isActive = st.status === "in-transit" || st.status === "in-production";
                    const isDelayed = st.status === "delayed";
                    return (
                      <div key={st.stage} className="relative">
                        <div className={cn("p-2.5 rounded-sm border text-center transition-colors",
                          isComplete ? "border-success/40 bg-success/10"
                          : isDelayed ? "border-critical/40 bg-critical/10"
                          : isActive ? "border-primary/40 bg-primary/10"
                          : "border-border bg-surface")}>
                          <Icon size={14} className={cn("mx-auto mb-1",
                            isComplete ? "text-success" : isDelayed ? "text-critical" : isActive ? "text-primary" : "text-muted")} />
                          <div className="text-[10px] font-medium">{st.stage}</div>
                          <div className="text-[9px] text-muted font-mono-tabular mt-0.5">{st.actual || st.planned}</div>
                          {st.vessel && <div className="text-[8px] text-primary mt-0.5 truncate" title={st.vessel}>{st.vessel}</div>}
                        </div>
                        {i < po.stages.length - 1 && (
                          <div className={cn("absolute top-1/2 -right-0.5 w-1 h-0.5 z-10",
                            isComplete ? "bg-success" : "bg-border")} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Tile>

        {/* Last-Mile Delivery Route Optimization */}
        <Tile className="col-span-12 lg:col-span-8" testid="delivery-routes" origin={syn("last-mile routes")}
              title="Last-Mile Delivery Route Optimization — Today's Routes"
              action={<Badge status="success">{ROUTE_SAVINGS.fuelSavedL}L fuel saved · {formatPeso(ROUTE_SAVINGS.costSaved)} cost</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Route</th>
                <th className="py-2 px-3 text-left">Vehicle / Driver</th>
                <th className="py-2 px-3 text-right">Stops</th>
                <th className="py-2 px-3 text-right">KM Plan/Actual</th>
                <th className="py-2 px-3 text-right">Fuel Saved</th>
                <th className="py-2 px-3 text-right">Util</th>
                <th className="py-2 px-3 text-right">ETA</th>
                <th className="py-2 px-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {DELIVERY_ROUTES.map(rt => (
                <tr key={rt.id} className="border-b border-border" data-testid={`route-${rt.id}`}>
                  <td className="py-2 px-3 font-sans">{rt.id}</td>
                  <td className="py-2 px-3 font-sans">
                    <div className="font-medium text-sm">{rt.vehicle}</div>
                    <div className="text-[10px] text-muted">{rt.driver}</div>
                  </td>
                  <td className="py-2 px-3 text-right">{rt.stops}</td>
                  <td className="py-2 px-3 text-right">{rt.kmPlanned}/{rt.kmActual}km</td>
                  <td className={cn("py-2 px-3 text-right font-semibold", rt.fuelSavedL >= 0 ? "text-success" : "text-critical")}>
                    {rt.fuelSavedL >= 0 ? "+" : ""}{rt.fuelSavedL}L
                  </td>
                  <td className="py-2 px-3 text-right">{(rt.utilization * 100).toFixed(0)}%</td>
                  <td className="py-2 px-3 text-right text-primary font-semibold">{rt.eta}</td>
                  <td className="py-2 px-3 font-sans">
                    <Badge status={rt.status === "complete" ? "success" : rt.status === "delayed" ? "warning" : "info"}>{rt.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        <Tile className="col-span-12 lg:col-span-4" testid="route-savings" origin={syn("routing savings")} title="AI Routing Savings vs Manual">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Fuel Saved (L)" value={ROUTE_SAVINGS.fuelSavedL} status="success" />
            <Stat label="KM Saved" value={ROUTE_SAVINGS.kmSaved} status="success" />
            <Stat label="Time Saved (min)" value={ROUTE_SAVINGS.timeSavedMin} status="success" />
            <Stat label="₱ Saved Today" value={formatPeso(ROUTE_SAVINGS.costSaved, { compact: true })} status="success" />
          </div>
          <hr className="my-4 border-border" />
          <div className="text-center">
            <div className="kpi-label">Efficiency Lift vs Manual Planning</div>
            <div className="kpi-value text-4xl mt-2 text-primary">+{(ROUTE_SAVINGS.vsManualPlanning * 100).toFixed(0)}%</div>
            <div className="text-[10px] text-muted mt-2 font-mono-tabular">Annualized: ₱8.4M fuel + ₱2.1M driver time</div>
          </div>
        </Tile>
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div className="p-2 border border-border rounded-sm bg-surface-2/40">
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-lg mt-1">{value}</div>
    </div>
  );
}

function Agg({ label, value, status, icon, testid, origin }) {
  return (
    <div className="px-4 py-3" data-testid={testid}>
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{icon}{label}
        <OriginBadge {...origin} className="ml-auto" testid={`${testid}-origin`} /></div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
